import { Button, faDigits, Field, Panel } from "@kitchen/ui";
import { useState } from "react";
import { api } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";
import { useComponents, useItems, useMeals } from "../menu/data.js";
import {
  type KitchenProfileRow,
  type MealTimesRow,
  useMealTimes,
  useProfiles,
  useSites,
  useTravel,
} from "./data.js";

const toInt = (s: string) => {
  const n = Number.parseInt(
    s.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))),
    10,
  );
  return Number.isFinite(n) ? n : null;
};

/**
 * F4-T03 screen: everything the planner needs that the manager sets once (architecture §8.5, DL-053, DL-054):
 * per kitchen the day start, cook minutes and pot size per component; travel minutes between sites; the usual
 * ready / handover / packing times of each meal. Every number here is the manager's, nothing is fixed in code.
 */
export function ProductionSettings() {
  return (
    <div className="stack">
      <MealTimesPanel />
      <KitchensPanel />
      <TravelPanel />
    </div>
  );
}

function MealTimesPanel() {
  const meals = useMealTimes();
  const all = useMeals();
  const sites = useSites();
  const live = (sites.data ?? []).filter((s) => s.active);
  return (
    <Panel title="زمان‌های معمول هر وعده">
      <div className="stack">
        <p className="t-cap muted">
          «آماده شود تا» و «تحویل به راننده تا» و جای بسته‌بندی هر وعده. هر روز در برنامه‌ی تولید قابل تغییر
          است.
        </p>
        {(all.data ?? []).map((m) => (
          <MealTimesRowEditor
            key={m.id}
            mealId={m.id}
            mealName={m.name}
            row={meals.data?.find((x) => x.mealId === m.id) ?? null}
            sites={live}
          />
        ))}
      </div>
    </Panel>
  );
}

function MealTimesRowEditor({
  mealId,
  mealName,
  row,
  sites,
}: {
  mealId: string;
  mealName: string;
  row: MealTimesRow | null;
  sites: { id: string; name: string }[];
}) {
  const [readyBy, setReadyBy] = useState(row?.readyBy ?? "");
  const [handoverBy, setHandoverBy] = useState(row?.handoverBy ?? "");
  const [packAt, setPackAt] = useState(row?.packing.packAt ?? "");
  const [siteId, setSiteId] = useState(row?.packing.siteId ?? sites[0]?.id ?? "");
  const save = useAction(
    () =>
      api(`/production/settings/meals/${mealId}`, {
        method: "PUT",
        body: {
          version: row?.version ?? null,
          readyBy,
          handoverBy,
          packing: { siteId: siteId || sites[0]?.id, packAt },
        },
      }),
    [["production-meals"], ["production-plan"]],
  );
  const ok = /^\d\d:\d\d$/.test(readyBy) && /^\d\d:\d\d$/.test(handoverBy) && /^\d\d:\d\d$/.test(packAt);
  return (
    <form
      className="row wrap"
      style={{ gap: "var(--s3)", alignItems: "end" }}
      onSubmit={(e) => {
        e.preventDefault();
        save.run(undefined);
      }}
    >
      <strong style={{ minWidth: 80 }}>{mealName}</strong>
      <Field
        id={`ready-${mealId}`}
        label="آماده شود تا"
        type="time"
        value={readyBy}
        onChange={(e) => {
          setReadyBy(e.target.value);
        }}
      />
      <Field
        id={`handover-${mealId}`}
        label="تحویل به راننده تا"
        type="time"
        value={handoverBy}
        onChange={(e) => {
          setHandoverBy(e.target.value);
        }}
      />
      <div className="field">
        <label htmlFor={`pack-site-${mealId}`}>بسته‌بندی در</label>
        <select
          id={`pack-site-${mealId}`}
          className="input"
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
          }}
        >
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <Field
        id={`pack-at-${mealId}`}
        label="ساعت بسته‌بندی"
        type="time"
        value={packAt}
        onChange={(e) => {
          setPackAt(e.target.value);
        }}
      />
      <Button type="submit" variant={row ? "ghost" : "primary"} icon="check" disabled={!ok || save.busy}>
        {row ? "ذخیره" : "تعیین زمان‌ها"}
      </Button>
      <ErrorLine error={save.error} />
    </form>
  );
}

function KitchensPanel() {
  const profiles = useProfiles();
  const components = useComponents();
  const items = useItems();
  // Foods without components are cooked as one piece under their own name (AS-035).
  const pieces = [
    ...(components.data ?? []).filter((c) => c.active).map((c) => ({ id: c.id, name: c.name })),
    ...(items.data ?? [])
      .filter((i) => i.kind === "food" && i.active)
      .map((i) => ({ id: i.id, name: `${i.name} (کل غذا)` })),
  ];
  return (
    <Panel title="آشپزخانه‌ها: ساعت شروع، مدت پخت و ظرفیت دیگ">
      <div className="stack">
        <p className="t-cap muted">
          مدت پخت هر جزء در هر آشپزخانه و اینکه هر دیگ چند پرس جا دارد. اگر خالی بماند، مدت پیش‌فرض آشپزخانه
          به کار می‌رود و برنامه هشدار می‌دهد. آشپزخانه‌ی تازه را در «آشپزخانه‌ها و ایستگاه‌ها» بسازید.
        </p>
        {(profiles.data ?? []).map((p) => (
          <KitchenEditor key={`${p.siteId}:${String(p.version)}`} profile={p} pieces={pieces} />
        ))}
      </div>
    </Panel>
  );
}

function KitchenEditor({
  profile,
  pieces,
}: {
  profile: KitchenProfileRow;
  pieces: { id: string; name: string }[];
}) {
  const [dayStart, setDayStart] = useState(profile.dayStart);
  const [defaultCook, setDefaultCook] = useState(String(profile.defaultCookMinutes));
  const [cook, setCook] = useState<Record<string, number>>(profile.cookMinutes);
  const [pots, setPots] = useState<Record<string, number>>(profile.potCapacity);
  const save = useAction(
    () =>
      api(`/production/settings/kitchens/${profile.siteId}`, {
        method: "PUT",
        body: {
          version: profile.version,
          dayStart,
          defaultCookMinutes: toInt(defaultCook) ?? 60,
          cookMinutes: cook,
          potCapacity: pots,
        },
      }),
    [["production-kitchens"], ["production-plan"]],
  );
  const setNum = (
    map: Record<string, number>,
    set: (m: Record<string, number>) => void,
    id: string,
    v: string,
  ) => {
    const n = toInt(v);
    const next = Object.fromEntries(Object.entries(map).filter(([k]) => k !== id));
    if (n && n > 0) next[id] = n;
    set(next);
  };
  return (
    <details className="card" open={profile.version === null}>
      <summary className="row" style={{ gap: "var(--s3)", cursor: "pointer" }}>
        <strong>{faDigits(profile.siteName)}</strong>
        {!profile.active && <span className="muted t-cap">غیرفعال</span>}
        <span className="muted t-cap">
          شروع کار {faDigits(dayStart)} · {faDigits(Object.keys(cook).length)} جزء با مدت پخت
        </span>
      </summary>
      <div className="stack" style={{ marginTop: "var(--s3)" }}>
        <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "end" }}>
          <Field
            id={`day-${profile.siteId}`}
            label="شروع کار آشپزخانه"
            type="time"
            value={dayStart}
            onChange={(e) => {
              setDayStart(e.target.value);
            }}
          />
          <Field
            id={`def-${profile.siteId}`}
            label="مدت پخت پیش‌فرض (دقیقه)"
            inputMode="numeric"
            value={faDigits(defaultCook)}
            onChange={(e) => {
              setDefaultCook(e.target.value);
            }}
            style={{ width: 120 }}
          />
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>جزء</th>
                <th>مدت پخت (دقیقه)</th>
                <th>هر دیگ چند پرس</th>
              </tr>
            </thead>
            <tbody>
              {pieces.map((c) => (
                <tr key={c.id}>
                  <td>{faDigits(c.name)}</td>
                  <td>
                    <input
                      className="input num"
                      style={{ width: 90 }}
                      inputMode="numeric"
                      aria-label={`مدت پخت ${c.name}`}
                      defaultValue={cook[c.id] ? faDigits(cook[c.id] ?? 0) : ""}
                      placeholder={faDigits(defaultCook)}
                      onBlur={(e) => {
                        setNum(cook, setCook, c.id, e.target.value);
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="input num"
                      style={{ width: 90 }}
                      inputMode="numeric"
                      aria-label={`ظرفیت دیگ ${c.name}`}
                      defaultValue={pots[c.id] ? faDigits(pots[c.id] ?? 0) : ""}
                      placeholder="—"
                      onBlur={(e) => {
                        setNum(pots, setPots, c.id, e.target.value);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row">
          <span className="grow" />
          <Button
            variant="primary"
            icon="check"
            disabled={save.busy}
            onClick={() => {
              save.run(undefined);
            }}
          >
            ذخیره‌ی {faDigits(profile.siteName)}
          </Button>
        </div>
        <ErrorLine error={save.error} />
      </div>
    </details>
  );
}

function TravelPanel() {
  const sites = useSites();
  const travel = useTravel();
  const live = (sites.data ?? []).filter((s) => s.active);
  const save = useAction(
    (t: { fromSiteId: string; toSiteId: string; minutes: number }) =>
      api("/production/settings/travel", { method: "PUT", body: t }),
    [["production-travel"], ["production-plan"]],
  );
  const minutes = (from: string, to: string) =>
    travel.data?.find((t) => t.fromSiteId === from && t.toSiteId === to)?.minutes;
  return (
    <Panel title="زمان راه بین مکان‌ها (دقیقه)">
      <div className="stack">
        <p className="t-cap muted">
          ردیف: مبدأ، ستون: مقصد. اگر فقط یک جهت را بنویسید، برای جهت برعکس هم همان به کار می‌رود.
        </p>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>از ↓ / به ←</th>
                {live.map((s) => (
                  <th key={s.id}>{faDigits(s.name)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {live.map((from) => (
                <tr key={from.id}>
                  <th>{faDigits(from.name)}</th>
                  {live.map((to) => (
                    <td key={to.id}>
                      {from.id === to.id ? (
                        <span className="muted">—</span>
                      ) : (
                        <input
                          key={`${from.id}-${to.id}-${String(minutes(from.id, to.id) ?? "")}`}
                          className="input num"
                          style={{ width: 80 }}
                          inputMode="numeric"
                          aria-label={`راه ${from.name} تا ${to.name}`}
                          defaultValue={
                            minutes(from.id, to.id) === undefined
                              ? ""
                              : faDigits(minutes(from.id, to.id) ?? 0)
                          }
                          placeholder={
                            minutes(to.id, from.id) === undefined
                              ? ""
                              : faDigits(minutes(to.id, from.id) ?? 0)
                          }
                          onBlur={(e) => {
                            const n = toInt(e.target.value);
                            if (n !== null && n !== minutes(from.id, to.id))
                              save.run({ fromSiteId: from.id, toSiteId: to.id, minutes: n });
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ErrorLine error={save.error} />
      </div>
    </Panel>
  );
}
